import { createSelector } from '@reduxjs/toolkit';
import { CAPSTONE } from '@rsos/constants/appNames';
import { selectUserPermissionsByApp } from '@rsos/sinatra';
import type { RootState } from '../../../app/rootReducer';
import hasMedicAlertAccess from '../../../utils/hasMedicAlertAccess';
import type {
  RadeCardItem,
  RadeLookupKind,
  RadeLookupStatus,
} from './radeSlice';

/**
 * Projection of the RAD-e Redux state that `RADECardNext` actually
 * needs to render. Collapses `selectedLookup` + the chosen channel +
 * access gating into a single typed object so the component is a
 * pure render of the result.
 */
export interface RadeNextView {
  kind: RadeLookupKind;
  status: RadeLookupStatus;
  data: RadeCardItem[] | null;
}

const selectSelectedLookup = (state: RootState) => state.rade.selectedLookup;
const selectMafLookup = (state: RootState) => state.rade.mafLookup;
const selectAdrLookup = (state: RootState) => state.rade.adrLookup;
const selectCapstonePermissions = (state: RootState) =>
  selectUserPermissionsByApp(state.sinatra.user, CAPSTONE);

/**
 * Returns the view `RADECardNext` should render, or `null` when:
 * - no lookup is selected,
 * - the selected channel hasn't been kicked off yet, or
 * - the user lacks the access required for that channel.
 *
 * MAF lookups are gated by MedicAlert access; ADR lookups are
 * ungated (matches the legacy `ConnectedUniversalSearch` behavior).
 *
 * Memoized via `createSelector` so unrelated `radeSlice` writes
 * (e.g. flipping `triggerSearch` for the legacy card) don't
 * re-render the container.
 */
export const selectRadeNextView = createSelector(
  [
    selectSelectedLookup,
    selectMafLookup,
    selectAdrLookup,
    selectCapstonePermissions,
  ],
  (selectedLookup, mafLookup, adrLookup, permissions): RadeNextView | null => {
    if (selectedLookup === 'maf') {
      if (!mafLookup) return null;
      if (!hasMedicAlertAccess(permissions)) return null;
      return { kind: 'maf', status: mafLookup.status, data: mafLookup.data };
    }

    if (selectedLookup === 'adr') {
      if (!adrLookup) return null;
      return { kind: 'adr', status: adrLookup.status, data: adrLookup.data };
    }

    return null;
  },
);
